// Merge các field scalar/permissions của pack template vào project settings:
// disableWorkflows, statusLine, includeCoAuthoredBy, model, env (+domain-rewrite),
// permissions. Tách từ merge-pack-settings-into-project-settings.ts (A6 refactor)
// — lift NGUYÊN VĂN step 0-5, mutate merged/changes tại chỗ (behavior-preserving).
// async vì statusLine phải await isStatusLineCommandResolvable.

import type { PackSettingsTemplate } from "./merge-settings-types.js";
import { isStatusLineCommandResolvable } from "./settings-command-path-safety.js";
import { unionDedupe } from "./settings-merge-primitives.js";

export async function applyScalarFieldMerges(
  merged: PackSettingsTemplate,
  changes: string[],
  packTemplate: PackSettingsTemplate,
  userSettings: PackSettingsTemplate,
  workspacePath: string,
): Promise<void> {
  // 0. Avatar policy: tắt Dynamic Workflows của Claude Code trong workspace Avatar.
  // Áp cho CẢ workspace cũ (init trước khi có policy này) — nếu user CHƯA tự set
  // field này, Avatar set true. User chủ động đặt false → tôn trọng, không đè.
  if (typeof userSettings.disableWorkflows !== "boolean") {
    merged.disableWorkflows = true;
    changes.push("disableWorkflows=true (Avatar tắt Dynamic Workflows)");
  }

  // 1. statusLine — pack wins nếu user chưa set, NHƯNG verify file ref tồn tại.
  // v1.7.0 fix: trước đây merge blindly → settings ref `node .claude/statusline.cjs`
  // nhưng pack KHÔNG ship file đó → Claude Code fail im lặng render statusbar.
  // Giờ check: command pack ref tồn tại trong workspace? Nếu không → skip + warn.
  if (packTemplate.statusLine && !userSettings.statusLine) {
    const statusLineOk = await isStatusLineCommandResolvable(
      workspacePath,
      packTemplate.statusLine.command,
    );
    if (statusLineOk) {
      merged.statusLine = packTemplate.statusLine;
      changes.push("statusLine added");
    } else {
      changes.push(
        `statusLine SKIPPED (file ref '${packTemplate.statusLine.command}' không tồn tại)`,
      );
    }
  }

  // 2. includeCoAuthoredBy — pack wins nếu user chưa set.
  if (
    typeof packTemplate.includeCoAuthoredBy === "boolean" &&
    typeof userSettings.includeCoAuthoredBy !== "boolean"
  ) {
    merged.includeCoAuthoredBy = packTemplate.includeCoAuthoredBy;
    changes.push("includeCoAuthoredBy added");
  }

  // 3. model — user wins; chỉ add nếu pack có và user chưa set.
  if (packTemplate.model && !userSettings.model) {
    merged.model = packTemplate.model;
    changes.push("model added");
  }

  // 4. env — user wins entirely (giữ API key). Chỉ add var pack có mà user chưa có.
  // v1.20.0: Auto-rewrite legacy domain llm.nal.vn → ai.nal.vn nếu user có
  // settings.json từ pre-v1.20.0 (NAL gateway đổi domain). Silent migration,
  // không cần user confirm — cùng infra rename.
  if (packTemplate.env || userSettings.env) {
    const mergedEnv: Record<string, string> = { ...(userSettings.env || {}) };
    // 2 cờ RIÊNG để message không lẫn nhau: domain-rewrite và pack-var-added là
    // 2 thay đổi độc lập, mỗi cái log message riêng (trước đây dùng chung 1 cờ +
    // substring match "env vars" khiến message "added from pack" bị nuốt khi
    // đồng thời có domain rewrite).
    let domainRewritten = false;
    let packVarsAdded = false;
    // Domain rewrite trên mọi string value
    for (const [k, v] of Object.entries(mergedEnv)) {
      if (typeof v === "string" && v.includes("llm.nal.vn")) {
        mergedEnv[k] = v.replace(/llm\.nal\.vn/g, "ai.nal.vn");
        domainRewritten = true;
      }
    }
    if (domainRewritten) {
      changes.push("rewrote legacy llm.nal.vn → ai.nal.vn in env vars");
    }
    // Pack env vars add nếu user chưa có
    if (packTemplate.env) {
      for (const [k, v] of Object.entries(packTemplate.env)) {
        if (!(k in mergedEnv)) {
          mergedEnv[k] = v;
          packVarsAdded = true;
        }
      }
    }
    if (packVarsAdded) {
      changes.push("env vars added from pack");
    }
    if (domainRewritten || packVarsAdded) {
      merged.env = mergedEnv;
    }
  }

  // 5. permissions — union allow + ask + deny (dedupe), GIỮ key khác của user.
  // Bản trước chỉ union allow/deny và ghi lại `{allow, deny}` ⇒ `ask` của pack bị bỏ
  // rơi, `ask`/`defaultMode` user tự đặt bị XOÁ mỗi lần sync (đo 2026-09-09 khi đưa
  // rule "hỏi khi sửa .env" vào template). `ask` được Claude Code áp cả ở bypass mode
  // nên là cơ chế đúng cho rào mềm — merge phải mang nó sang.
  if (packTemplate.permissions) {
    const userPerm = userSettings.permissions || {};
    const userAllow = userPerm.allow || [];
    const userAsk = userPerm.ask || [];
    const userDeny = userPerm.deny || [];
    const packAllow = packTemplate.permissions.allow || [];
    const packAsk = packTemplate.permissions.ask || [];
    const packDeny = packTemplate.permissions.deny || [];
    const mergedAllow = unionDedupe(userAllow, packAllow);
    const mergedAsk = unionDedupe(userAsk, packAsk);
    const mergedDeny = unionDedupe(userDeny, packDeny);
    const addedAllow = mergedAllow.length - userAllow.length;
    const addedAsk = mergedAsk.length - userAsk.length;
    const addedDeny = mergedDeny.length - userDeny.length;
    if (addedAllow || addedAsk || addedDeny) {
      // Spread userPerm TRƯỚC: defaultMode/additionalDirectories… của user sống sót.
      // Không tạo key `ask` rỗng — settings cũ không có ask thì file không đổi shape.
      merged.permissions = {
        ...userPerm,
        allow: mergedAllow,
        deny: mergedDeny,
        ...(mergedAsk.length ? { ask: mergedAsk } : {}),
      };
      changes.push(
        `permissions union (+${addedAllow} allow, +${addedDeny} deny${addedAsk ? `, +${addedAsk} ask` : ""})`,
      );
    }
  }
}
